import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { TFunction } from 'i18next';
import {
  MAX_ROOMS_CHARACTERS_DISPLAYED_SM_OR_UP,
  MAX_ROOMS_CHARACTERS_DISPLAYED_XS,
} from '../helpers';
import { emojiPattern } from '../utils';
import { CompanionRoom } from './model';
import { PeriodsOfDay, TimesOfDay } from './types';

dayjs.extend(isBetween);

export { getSuggestedCountryCode } from './countrySuggestion';

const getFirstCharacter = (word: string) => {
  const firstTwoCharacters = word.substring(0, 2);
  if (emojiPattern.test(firstTwoCharacters)) {
    return firstTwoCharacters;
  }
  return word[0];
};

export const getDisplayNameInitials = (memberDisplayName?: string) =>
  memberDisplayName
    ?.split(' ', 2)
    .map((word) => getFirstCharacter(word))
    .join('')
    .toUpperCase();

export const getNumberOfHiddenRooms = (
  rooms: CompanionRoom[] | undefined,
  isMobileOrUp: boolean
) => {
  let hiddenRoomsCounter = 0;
  let maxPossibleCharacters = isMobileOrUp
    ? MAX_ROOMS_CHARACTERS_DISPLAYED_SM_OR_UP
    : MAX_ROOMS_CHARACTERS_DISPLAYED_XS;

  rooms &&
    rooms.forEach((room) => {
      if (
        maxPossibleCharacters - room.title.length > 0 &&
        maxPossibleCharacters !== 0
      ) {
        maxPossibleCharacters = maxPossibleCharacters - room.title.length - 2;
      } else {
        maxPossibleCharacters = 0;
        hiddenRoomsCounter = hiddenRoomsCounter + 1;
      }
    });

  return hiddenRoomsCounter;
};

const getPeriodByTime = (time = dayjs()) => {
  const morningStart = dayjs().set('hour', TimesOfDay.Morning).startOf('hour');
  const afternoonStart = dayjs()
    .set('hour', TimesOfDay.Afternoon)
    .startOf('hour');
  const eveningStart = dayjs().set('hour', TimesOfDay.Evening).startOf('hour');

  if (time.isBetween(morningStart, afternoonStart)) {
    return PeriodsOfDay.Morning;
  } else if (time.isBetween(afternoonStart, eveningStart)) {
    return PeriodsOfDay.Afternoon;
  }
  return PeriodsOfDay.Evening;
};

export const getPeriodOfDay = (t: TFunction, date?: string) => {
  const time = date ? dayjs(date) : dayjs();
  const period = getPeriodByTime(time);
  return t(`wellness_tracker_${period}`);
};

// Keep aligned with TemporaryDisplayNameAllocator.Words in carecircleLibrary.
const TEMPORARY_DISPLAY_NAME_WORDS = new Set(
  [
    'Amber', 'Apple', 'Aspen', 'Autumn', 'Azure', 'Bamboo', 'Beacon', 'Birch', 'Blossom', 'Breeze',
    'Bright', 'Brook', 'Calm', 'Canyon', 'Cedar', 'Cheerful', 'Cloud', 'Clover', 'Comet', 'Coral',
    'Cosmic', 'Cozy', 'Crystal', 'Daisy', 'Dawn', 'Dolphin', 'Dream', 'Echo', 'Ember', 'Falcon',
    'Fern', 'Firefly', 'Forest', 'Gentle', 'Glacier', 'Golden', 'Harbor', 'Harmony', 'Harp', 'Hazel',
    'Honey', 'Horizon', 'Island', 'Ivory', 'Jade', 'Jasmine', 'Jolly', 'Juniper', 'Lagoon', 'Lantern',
    'Lark', 'Lavender', 'Lemon', 'Lily', 'Lotus', 'Lucky', 'Lunar', 'Maple', 'Meadow', 'Melody',
    'Mint', 'Misty', 'Moon', 'Noble', 'Oak', 'Ocean', 'Olive', 'Orchid', 'Otter', 'Pearl',
    'Pebble', 'Pine', 'Planet', 'Poppy', 'Prairie', 'Quiet', 'Rainbow', 'Ripple', 'River', 'Robin',
    'Rose', 'Sage', 'Sapphire', 'Serene', 'Silver', 'Sky', 'Space', 'Sparrow', 'Spring', 'Star',
    'Summit', 'Sunny', 'Swift', 'Tulip', 'Ubiquitous', 'Valley', 'Velvet', 'Willow', 'Wren', 'Zephyr',
  ].map((word) => word.toLowerCase())
);

/**
 * Standalone registrations reserve a temporary display name of two different
 * listed words plus a number from 10 to 99 (e.g. "Rose Harp 43"). Older members
 * may still have the legacy member GUID first segment (plus a numeric collision
 * suffix). Either is a handle, never a personal name.
 */
export const isTemporaryMemberDisplayName = (
  displayName: string | undefined,
  memberId: string | undefined
) => {
  const name = displayName?.trim().toLowerCase();
  if (!name) return false;
  const words = /^([a-z]+) ([a-z]+) [1-9]\d$/.exec(name);
  if (words && words[1] !== words[2] && TEMPORARY_DISPLAY_NAME_WORDS.has(words[1]) && TEMPORARY_DISPLAY_NAME_WORDS.has(words[2]))
    return true;
  const prefix = memberId?.split('-')[0]?.toLowerCase();
  return !!prefix && name.startsWith(prefix) && /^\d*$/.test(name.slice(prefix.length));
};

/**
 * Name to address the member by: their profile first name, then a display name
 * they chose. Returns undefined when neither exists so callers use unnamed text.
 */
export const getMemberPersonalName = (
  member: { id?: string; memberDisplayName?: string } | undefined,
  user?: { firstName?: string } | undefined
) => {
  const firstName = user?.firstName?.trim();
  if (firstName && !isTemporaryMemberDisplayName(firstName, member?.id)) return firstName;
  const displayName = member?.memberDisplayName?.trim();
  return displayName && !isTemporaryMemberDisplayName(displayName, member?.id) ? displayName : undefined;
};
